import { QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@milliswift/auth'
import { APP_URLS, createQueryClient, installMocks } from '@milliswift/api-client'
import { ErrorBoundary, ToastProvider } from '@milliswift/ui'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import ForgotPassword from './pages/ForgotPassword'
import Invite from './pages/Invite'
import Login from './pages/Login'
import ResetPassword from './pages/ResetPassword'
import Signup from './pages/Signup'
import VerifyEmail from './pages/VerifyEmail'

const queryClient = createQueryClient()
installMocks()

/** Where a successful sign-in lands. Honors ?return_to across apps. */
function usePostLoginTarget(): string {
  const { search } = useLocation()
  const returnTo = new URLSearchParams(search).get('return_to')
  if (returnTo && /^https?:\/\//.test(returnTo)) {
    // Only same-host milliswift origins; anything else is suspicious.
    try {
      const url = new URL(returnTo)
      if (url.hostname.endsWith('.milliswift.com') || url.hostname === 'localhost') {
        return url.toString()
      }
    } catch {
      // fall through to the default
    }
  }
  return `${APP_URLS.platform}/dashboard`
}

export default function App() {
  const postLogin = usePostLoginTarget()

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider redirectOnUnauthorized={false}>
            <Routes>
              <Route path="/login" element={<Login postLoginTarget={postLogin} />} />
              <Route path="/signup" element={<Signup postLoginTarget={postLogin} />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/invite/:token" element={<Invite />} />
              {/* Aliases so / works in dev without a redirect loop. */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
